// DEPENDENCIES
import { FormikProps, FormikValues } from "formik";
import { doc } from "firebase/firestore";

// TYPES
import {
  Template,
  PersonType,
  DraftUUID,
  ComponentStep,
  StepFivePF,
  PreconfirmationStep,
  CoownerData,
  PitbullCountry,
  Step,
  StepOnePF,
  StepFourPF,
  StepTwoPF,
  StepSixPF,
  StepEndpoint,
  Option,
  StepThreePF,
  PitbullProvinces,
  BasicStep,
  CoownerSteps,
} from "../../types";
import { AppDispatch, RootState, store } from "../../app/store";

// UTILS
import { logAnalyticsAndHotjarEvent } from "../../main";
import {
  checkboxRequired,
  checkbox,
  isAdditionalCoownerCuitExcludedFrom,
} from "../../validations";
import {
  getParsedEmailFromUrl,
  getParsedRenaperData,
  goToStep,
  handleSendStep,
  onContinueRenaper,
  parseBirthDate,
  buildFullAddress,
} from "../../app/utils";
import {
  addAdditionalSentStep,
  onUpdateFormData,
} from "../../reducers/onboardingDataReducer";
import {
  hasRenaperData,
  asCuit,
  checkIsEmailAvailable,
  completePhysicalSteps,
  deleteDniImages,
  deleteLiveness,
  getDniFromCuit,
  getFormattedPreconfirmationFields,
  isStepOneSent,
  loadRemoteIdentityProgress,
  promptRemoteIdentityResume,
} from "../../utils";
import getTemplateData from "../../hooks/useTemplate";
import {
  deleteCoownerDraft,
  getDraft,
  sendIndividualDocument,
} from "../../connectors/connectors";
import { documentTypesParsed, firestoreCollections } from "../../constants";
import { getCompanyData } from "../../utils/companies";
import { normalizePhysicalStepsForApi } from "../../steps/shared/qualifiedInvestorField";

//  COMPONENTS
import UploadDniCard, {
  DniStep,
} from "../../components/UploadDniCard/UploadDniCard";
import LivenessValidationCard from "../../components/LivenessValidationCard/LivenessValidationCard";
import { hideLivenessStepHeader } from "../../components/LivenessValidationCard/utils/utils";
import PreloadTemplateLanding from "../../components/PreloadTemplateLanding/PreloadTemplateLanding";

// STEPS
import infoStep from "../../steps/physical/infoStep";
import tokenStep from "../../steps/physical/tokenStep";
import employmentProfileStep from "../../steps/physical/employmentProfileStep";
import investmentProfileStep from "../../steps/physical/investmentProfileStep";
import personalInformationStep from "../../steps/physical/personalInformationStep";
import coownersStep from "../../steps/physical/coownersStep";
import preconfirmationSteps, {
  coownersPreconfirmationStep,
} from "../../steps/physical/preconfirmationSteps";
import { onUpdateInfoStep } from "../../reducers/infoStepReducer";

import bodyIcon from "../../assets/images/icons/ui/body.svg";
import { fieldsFrom } from "../../inputs";
import { Icon } from "@allaria-tech/matter/Icon";

const ownerPreconfirmationSteps = preconfirmationSteps;

const onboardingAdditionalCoowner = async (
  dispatch: AppDispatch,
  draftUUID?: DraftUUID,
  formValues?: FormikValues | null,
): Promise<Template> => {
  try {
    const queryParams = new URLSearchParams(window.location.search);
    const uuid = queryParams.get("externalCode") || "";

    const {
      favicon,
      formTitle,
      logo,
      questions,
      heritageDocuments,
      countries,
      countriesOptions,
      citizenshipOptions,
      activitiesOptions,
      getProvincesFilteredByCountry,
      provinces,
    } = await getTemplateData({
      draftUUID: draftUUID || uuid,
      formValues,
    });

    if (heritageDocuments?.length) {
      dispatch(
        onUpdateFormData({
          heritageFiles: heritageDocuments,
        }),
      );
    }

    const sendCoownerStep = async (
      formValues: FormikValues | null,
      step: Record<
        string,
        StepOnePF | StepTwoPF | StepThreePF | StepFourPF | StepFivePF
      >,
    ) => {
      const firstCoownerOnboarding = formValues?.steps;
      const personalInfoStep: StepOnePF = firstCoownerOnboarding?.find(
        (step: StepOnePF) => step["@type"] === "1_PHYSICAL",
      );
      const firstCoownerEmail =
        personalInfoStep?.personalInformation.email || "";

      const newStep = {
        [Object.keys(step)[0]]: Object.values(step)[0],
      };

      const sentSteps =
        store.getState().onboardingData.additionalSentSteps || {};

      const onAddSentStep = async () => {
        dispatch(addAdditionalSentStep(newStep));
      };

      const mergedSteps = {
        ...sentSteps,
        ...newStep,
      };

      if (mergedSteps.stepFour) {
        mergedSteps.stepFour = {
          ...mergedSteps.stepFour,
          qualifiedInvestorType:
            mergedSteps.stepFour.qualifiedInvestorType ?? "NONE",
        };
      }

      const stepSix = {
        "@type": "6_PHYSICAL",
        coOwnerSteps: [mergedSteps],
      };

      await handleSendStep({
        dispatch,
        personType: "PHYSICAL" as PersonType,
        email: firstCoownerEmail,
        sendedSteps: normalizePhysicalStepsForApi(firstCoownerOnboarding || []),
        stepId: 6,
        step: stepSix,
        onAddSentStep,
      });
    };

    const steps = [
      preloadingStep,
      tokenStep({ step: 2, onAuthorize, oldAuth: true }),
      coownerIdentityStep(countries),
      personalInformationStep(
        countriesOptions,
        citizenshipOptions,
        getProvincesFilteredByCountry,
        provinces,
        sendCoownerStep,
      ),
      employmentProfileStep(
        activitiesOptions,
        countriesOptions,
        getProvincesFilteredByCountry,
        provinces,
        sendCoownerStep,
      ),
      coownersStep({
        disableAllFields: true,
        isFirstCoowner: false,
      }),
      investmentProfileStep(questions, sendCoownerStep),
    ];

    return {
      onboardingType: "ADDITIONAL_COOWNER",
      formTitle,
      logo,
      icon: bodyIcon,
      favicon,
      steps: [
        ...steps.map((step, index) => ({ step: index + 1, ...step })),
        infoStep({
          onResetDraft,
        }),
      ],
      remoteSteps: [
        {
          link: "/dni",
          step: coownerIdentityStep(countries).subSteps?.find(
            (step) => step.label === "uploadDNI",
          ) as ComponentStep,
        },
        {
          link: "/identidad",
          step: coownerIdentityStep(countries).subSteps?.find(
            ({ label }) => label === "livenessValidation",
          ) as ComponentStep,
        },
      ],
      preconfirmation: ({ values }) => ({
        steps: [
          ...ownerPreconfirmationSteps(
            countriesOptions,
            citizenshipOptions,
            activitiesOptions,
            getProvincesFilteredByCountry,
            provinces,
            questions,
            "COOWNER",
          ),
          coownersPreconfirmationStep({ values, isFirstCoowner: false }),
          firstCoownerPreconfirmationSteps({
            countriesOptions,
            citizenshipOptions,
            getProvincesFilteredByCountry,
            provinces,
            values,
          }),
        ],
        checkboxes: getCompanyData(formValues).termsAndConditions.map(
          (term) => ({
            ...term,
            initialValue: false,
            validation: term.required ? checkboxRequired : checkbox,
          }),
        ),
        sendTermsAndConditions: (state: RootState, values: FormikValues) =>
          handleSendTermsAndConditions(state, values, sendCoownerStep),
      }),
    };
  } catch (error) {
    throw new Error(error as string);
  }
};

const preloadingStep: ComponentStep = {
  step: 1,
  name: "Información básica",
  hideStepper: true,
  component: (dispatch, state, formik) => {
    if (!state || !dispatch || !formik)
      throw new Error("Error: state, dispatch or formik not found");

    return <PreloadTemplateLanding />;
  },
  disableBackNavigation: true,
};

// TOKEN STEP
const onAuthorize = async (
  draftUUID: string,
  dispatch: AppDispatch,
  formik: FormikProps<FormikValues>,
) => {
  const currentCoownerEmail = getParsedEmailFromUrl();
  if (!currentCoownerEmail) throw new Error("Email not found");

  const draft = await getDraft({ draftUUID });
  let onboardingSteps = draft.data.steps;

  onboardingSteps = normalizePhysicalStepsForApi(
    onboardingSteps.map((step: StepEndpoint) => {
      if (step["@type"] === "5_PHYSICAL") {
        const stepFive = step as StepFivePF;
        return {
          "@type": "5_PHYSICAL",
          acceptedTermsAndConditions:
            stepFive.acceptedOptionalTermsAndConditions,
          acceptedOptionalTermsAndConditions:
            stepFive.acceptedOptionalTermsAndConditions,
          wantsAllariaAccount: stepFive.wantsAllariaAccount, // To prevent undefined error
        };
      }
      return step;
    }),
  );

  dispatch(
    onUpdateFormData({
      steps: onboardingSteps,
    }),
  );
  // For preconfirmation
  formik?.setValues(
    (prevValues) => ({
      ...prevValues,
      steps: onboardingSteps,
    }),
    false,
  );

  const firstCoownerData: StepOnePF = onboardingSteps.find(
    (step: StepOnePF) => step["@type"] === "1_PHYSICAL",
  );
  const additionalCoowners: StepThreePF = onboardingSteps.find(
    (step: StepThreePF) => step["@type"] === "3_PHYSICAL",
  );
  const additionalCoownersOnboardings: StepSixPF = onboardingSteps.find(
    (step: StepSixPF) => step["@type"] === "6_PHYSICAL",
  );

  const coownersData = {
    coowners: [
      {
        name: firstCoownerData.personalInformation.name,
        lastName: firstCoownerData.personalInformation.lastName,
        email: firstCoownerData.personalInformation.email,
        idNumber:
          firstCoownerData.personalInformation.identityDocument.idNumber,
      },
      ...additionalCoowners.coOwners,
    ],
  };
  formik?.setValues(
    (prevValues) => ({
      ...prevValues,
      ...coownersData,
    }),
    false,
  );
  dispatch(onUpdateFormData(coownersData));

  const currentCoownerDraft = getCurrentAdditionalCoownerDraft(
    additionalCoownersOnboardings.coOwnerSteps,
    currentCoownerEmail,
  );

  if (currentCoownerDraft && formik) {
    const parsedData = Object.values(currentCoownerDraft).filter(
      (step) => step !== undefined && step !== null,
    );

    const onSendSteps = async (steps: CoownerSteps) => {
      dispatch(addAdditionalSentStep(steps));
    };

    await completePhysicalSteps({
      dispatch,
      formik,
      steps: parsedData,
      onSendSteps,
    });
    dispatch(onUpdateInfoStep("PENDING_REGISTRY_REMOTE"));
  } else if (formik) {
    await loadRemoteIdentityProgress(
      dispatch,
      draft.data,
      formik,
      "addresses",
      currentCoownerEmail,
    );
    promptRemoteIdentityResume(dispatch);
  }
};

const onResetDraft = async ({
  dispatch,
  draftUUID,
  formik,
}: {
  dispatch: AppDispatch;
  draftUUID: DraftUUID;
  formik: FormikProps<FormikValues>;
}) => {
  const { email } = formik.values;

  try {
    await deleteCoownerDraft();
  } catch (deleteError) {
    console.error("Error deleting coowner draft:", deleteError);
  }

  const uuid = `${draftUUID}-${email}`;
  await deleteDniImages({
    collection: firestoreCollections.dniCoowner,
    uuid,
  });
  await deleteLiveness({
    collection: firestoreCollections.livenessCoowner,
    uuid,
  });

  dispatch({ type: "reset-store" });
  formik.resetForm();

  goToStep({ dispatch, step: 1 });
  logAnalyticsAndHotjarEvent("onboarding_coowner_restart");
};

// ADDITIONAL COOWNER IDENTITY STEP
const coownerIdentityStep = (
  countries: PitbullCountry[],
): Omit<Step, "step"> => {
  const uploadDniCard = (
    dispatch: AppDispatch,
    state: RootState | undefined,
    formik: FormikProps<FormikValues> | undefined,
    currentDniStep?: DniStep,
  ) => (
    <UploadDniCard
      dispatch={dispatch}
      state={state}
      formik={formik}
      currentDniStep={currentDniStep}
      getDniRef={({ db, uuid, email }) =>
        doc(db, firestoreCollections.dniCoowner, `${uuid}-${email}`)
      }
      onUploadDni={async (type, file, uuid, cuit) => {
        await sendIndividualDocument({
          draftUUID: uuid,
          documentType: documentTypesParsed[type],
          file,
          cuit: cuit,
        });
      }}
    />
  );

  return {
    name: "Cuenta cotitular",
    titleOfStep: "Identidad",
    label: "identity",
    disableButtonBack: true,
    subSteps: [
      {
        step: 1,
        fields: [
          {
            name: "cuit",
            type: "cuit",
            label: "CUIT/CUIL",
            initialValue: "",
            placeholder: "XX-XXXXXXXX-X",
            onChange: async (
              _values,
              setFieldValue,
              _,
              setFieldTouched,
              element,
            ) => {
              const value = element && element.target.value;
              if (value) {
                await setFieldValue("identityDocument", getDniFromCuit(value));
                setFieldTouched && setFieldTouched("identityDocument", true);
              }
            },
            conditionalValidation: (state) => {
              if (!state) throw new Error("Error: state not found");
              const { formValues } = state.onboardingData;
              const email = formValues?.email || "";
              const coowners: CoownerData[] = formValues?.coowners || [];
              const additionalCoowners = coowners.filter(
                ({ email: coownerEmail }) => coownerEmail !== email,
              );
              const additionalCoownersIdNumbers = additionalCoowners.map(
                ({ idNumber }) => idNumber || "",
              );
              return isAdditionalCoownerCuitExcludedFrom(
                additionalCoownersIdNumbers,
              );
            },
            formatInPreConfirmation: (value) => (value ? asCuit(value) : "-"),
          },
        ],
      },
      {
        step: 2,
        label: "uploadDNI", // Used to identify the step in remoteSteps
        skipWhen: (_, state) => isStepOneSent(state),
        component: (dispatch, state, formik) =>
          uploadDniCard(dispatch, state, formik),
        insideForm: true,
        onContinue: async (values, dispatch, state) => {
          if (!state || !dispatch) return;

          const { completedRemote } = state.onboardingData;
          if (!completedRemote) return;
          if (!hasRenaperData(state)) return;
          const informationParsed = state.renaper;

          await checkIsEmailAvailable({
            dispatch,
            idNumber: values.identityDocument || getDniFromCuit(values.cuit),
          });

          const body = getParsedRenaperData(
            values,
            state,
            informationParsed,
            countries,
          );
          dispatch(onUpdateFormData({ ...body }));

          return {
            newValuesToForm: body,
          };
        },
      },
      {
        step: 3,
        subtitle: "Cargá fotos del frente de tu DNI",
        component: (dispatch, state, formik) =>
          uploadDniCard(dispatch, state, formik, "upload_front"),
        skipWhen: (_, state) =>
          state.onboardingData.completedRemote || isStepOneSent(state),
        insideForm: true,
      },
      {
        step: 4,
        subtitle: "Cargá fotos del dorso de tu DNI",
        component: (dispatch, state, formik) =>
          uploadDniCard(dispatch, state, formik, "upload_back"),
        skipWhen: (_, state) =>
          state.onboardingData.completedRemote || isStepOneSent(state),
        onContinue: async (values, dispatch, state) => {
          if (!state || !dispatch) return;

          if (!hasRenaperData(state)) return;
          const informationParsed = state.renaper;

          await checkIsEmailAvailable({
            dispatch,
            idNumber: values.identityDocument || getDniFromCuit(values.cuit),
          });

          const body = getParsedRenaperData(
            values,
            state,
            informationParsed,
            countries,
          );
          dispatch(onUpdateFormData({ ...body }));

          return {
            newValuesToForm: body,
          };
        },
        insideForm: true,
      },
      {
        step: 5,
        fields: fieldsFrom(
          [
            "identityDocument",
            {
              disabled: true,
              tooltip: {
                label: <Icon name="edit" />,
                modal: "modal-edit-dni",
                external: true,
              },
            },
          ],
          "tramitNumber",
          "gender",
        ),
        skipWhen: (_, state) => hasRenaperData(state) || isStepOneSent(state),
        onContinue: async (values: FormikValues, dispatch, state) => {
          if (!dispatch || !values || !state) return;
          return await onContinueRenaper(values, dispatch, state, countries);
        },
        disableBackNavigation: true,
        disableButtonBack: true,
      },
      {
        step: 6,
        label: "livenessValidation", // Used to identify the step in remoteSteps
        skipWhen: (_, state) => isStepOneSent(state),
        titleOfStep: "Validá tu identidad",
        hideFormHeader: hideLivenessStepHeader,
        component: (dispatch, state, formik) => (
          <LivenessValidationCard
            dispatch={dispatch}
            state={state}
            formik={formik}
            getLivenessRef={({ db, uuid, email }) =>
              doc(db, firestoreCollections.livenessCoowner, `${uuid}-${email}`)
            }
          />
        ),
        onBack: async (_, __, state) => {
          if (!state) return;

          const showCompleteDniDataStep = !hasRenaperData(state);

          return {
            backSubStep: showCompleteDniDataStep ? 5 : 4,
          };
        },
        insideForm: true,
      },
    ],
    disableBackNavigation: true,
  };
};

// PRECONFIRMATION
const firstCoownerPreconfirmationSteps = ({
  countriesOptions,
  citizenshipOptions,
  getProvincesFilteredByCountry,
  provinces,
  values,
}: {
  countriesOptions: Option[];
  citizenshipOptions: Option[];
  getProvincesFilteredByCountry: (country?: string) => PitbullProvinces;
  provinces: PitbullProvinces;
  values?: FormikValues | null;
}): PreconfirmationStep => {
  const firstCoownerValues: StepOnePF = values?.steps?.find(
    (step: StepEndpoint) => step["@type"] === "1_PHYSICAL",
  );

  const firstCoownerFields = (
    personalInformationStep(
      countriesOptions,
      citizenshipOptions,
      getProvincesFilteredByCountry,
      provinces,
    ).subSteps ?? []
  ).flatMap((subStep: BasicStep) => subStep.fields ?? []);

  const {
    personalInformation: {
      pep,
      pepDetails,
      addresses,
      spouse,
      birthDate,
      ...rest
    },
  } = firstCoownerValues;

  const spouseData = {
    spouseName: spouse?.name,
    spouseLastName: spouse?.lastName,
    spouseCuil: spouse?.cuit,
  };

  const addressesData = {
    country: addresses[0].country,
    fullAddress: buildFullAddress(
      addresses[0].street,
      addresses[0].number,
      addresses[0].city,
    ),
    zipCode: addresses[0].zipCode,
    state: addresses[0].state,
    municipality: addresses[0].city,
    floor: addresses[0].floor,
    apartment: addresses[0].apartment,
  };

  const parsedPep = pep ?? "NONE";
  const parsePepDetails =
    parsedPep && parsedPep !== "NONE" ? pepDetails : undefined;

  const firstCoownerValuesParsed = {
    birthDate: birthDate ? parseBirthDate(birthDate) : "",
    pepDetails: parsePepDetails,
    ...(addresses && addresses.length > 0 && { addresses: addressesData }),
    ...(spouse && spouseData),
    ...rest,
  };

  const firstCoownerFieldsFormatted = getFormattedPreconfirmationFields(
    firstCoownerFields,
    firstCoownerValuesParsed,
  );

  return {
    name: `Datos del cotitular 1`,
    columns: 4,
    fields: [
      {
        name: "cuit",
        label: "CUIT / CUIL",
        type: "cuit",
        formatInPreConfirmation: (value) => asCuit(value),
        isConditional: false,
        values: firstCoownerValuesParsed,
      },
      ...firstCoownerFieldsFormatted.slice(0, 9),
      ...firstCoownerFieldsFormatted.slice(11, 18),
      ...firstCoownerFieldsFormatted.slice(9, 11),
    ],
  };
};

const handleSendTermsAndConditions = async (
  state: RootState,
  values: FormikValues,
  sendCoownerStep: (
    formValues: FormikValues | null,
    step: Record<string, StepFivePF>,
  ) => Promise<void>,
) => {
  const {
    onboardingData: { formValues },
  } = state;

  const termsAndConditionsStep = getPreconfirmationStep(values);
  await sendCoownerStep(formValues, {
    stepFive: termsAndConditionsStep,
  });
};

const getPreconfirmationStep = (values: FormikValues): StepFivePF => {
  const step: StepFivePF = {
    "@type": "5_PHYSICAL",
    acceptedOptionalTermsAndConditions: values?.senebiAndExteriorNotes,
    acceptedTermsAndConditions: values?.termsAndConditions,
  };

  return step;
};

// HELPERS
const getCurrentAdditionalCoownerDraft = (
  coownersDrafts: CoownerSteps[],
  email: string,
) =>
  coownersDrafts.find(
    (onboarding) => onboarding.stepOne.personalInformation.email === email,
  );

export default onboardingAdditionalCoowner;
